import { camelCase } from 'lodash-es'

import { makeNumber, makeRatioPercent, makeSignedPercent } from '@/helpers/chartFormat'
import { deltaToRatio } from '@/helpers/insightsMetrics'

import { metricState, requestState, type InsightsMetricState } from './insightsState'
import {
  SurveyResponsesStateEnum,
  type IncentivizationCampaignStatusEnum,
  type ReviewsInsightsGrowthQuery,
  type ReviewsInsightsLatestQuery,
  type ReviewsInsightsSurveyResponseFacetsQuery,
  type ReviewsTopRatedBadgeProgressQuery,
} from '../../reviews.generated'
import type { ProductById } from '../../types'

export type ReviewsQualityRingKey = 'completion' | 'response' | 'freshness'

export interface ReviewsQualityRing {
  key: ReviewsQualityRingKey
  value: number | null
  label: string
}

export interface ReviewsGrowthWindow {
  start: string
  end: string
  previousStart: string | null
  previousEnd: string | null
}

export interface ReviewsCategoryRow {
  label: string
  value: number
}

export type ReviewsCompanySizeKey = 'small' | 'medium' | 'large' | 'enterprise'

export type ReviewsSeniorityKey = 'cLevel' | 'leadership' | 'manager' | 'ic'

export type ReviewsSegmentDimension = 'seniority' | 'industry' | 'companySize'

export type ReviewsAttributeKey =
  | 'ux'
  | 'features'
  | 'performance'
  | 'reliability'
  | 'support'
  | 'pricing'
  | 'onboarding'
  | 'integrations'
  | 'reporting'
  | 'documentation'
  | 'customization'
  | 'collaboration'
  | 'mobile'
  | 'product_development'
  | 'security'

export interface ReviewsSegmentRow {
  key: string
  label: string | null
  share: number | null
  categoryShare: number | null
  nps: number | null
  other?: true
}

export interface ReviewsSegmentRows {
  rows: ReviewsSegmentRow[]
  shareState: InsightsMetricState
  npsState: InsightsMetricState
}

export interface ReviewsSentimentRow {
  key: ReviewsAttributeKey
  score: number
}

export interface ReviewsSentimentChip {
  key: ReviewsAttributeKey
  share: number
}

export interface ReviewsSentimentChips {
  positive: ReviewsSentimentChip[]
  negative: ReviewsSentimentChip[]
}

export interface ReviewsCampaignRow {
  id: number
  title: string
  status: IncentivizationCampaignStatusEnum | null
  validFrom: string | null
  validTo: string | null
  started: number
  submitted: number
  published: number
  rate: number | null
}

export interface ReviewsInsightsView {
  quality: { rings: ReviewsQualityRing[]; state: InsightsMetricState }
  timeframe: {
    totalReviews: { value: string; count: number | null; state: InsightsMetricState }
    newReviews: { value: string; delta: { value: string; direction: 'up' | 'down' } | null; state: InsightsMetricState }
    benchmark: { top3: number | null; top10: number | null; categoryAvg: number | null; median: number | null; state: InsightsMetricState }
    badge: {
      reviewCount: number
      minimumReviewCount: number
      averageScore: number | null
      eligible: boolean
      state: InsightsMetricState
    }
  }
  byCategory: { rows: ReviewsCategoryRow[]; weakest: string | null; state: InsightsMetricState }
  bySegment: {
    seniority: ReviewsSegmentRows
    industry: ReviewsSegmentRows
    companySize: ReviewsSegmentRows
  }
  sentiment: { rows: ReviewsSentimentRow[]; chips: ReviewsSentimentChips; state: InsightsMetricState }
  campaigns: { rows: ReviewsCampaignRow[]; state: InsightsMetricState }
}

export type ReviewsInsightsLoading = Record<
  | 'distribution'
  | 'aspects'
  | 'quality'
  | 'totalReviews'
  | 'newReviews'
  | 'badge'
  | 'byCategory'
  | 'bySegment'
  | 'sentiment'
  | 'history'
  | 'campaigns'
  | 'switchedFrom',
  boolean
>

export interface ReviewsInsightsSources {
  latest: ReviewsInsightsLatestQuery | null | undefined
  growth: ReviewsInsightsGrowthQuery | null | undefined
  facets: ReviewsInsightsSurveyResponseFacetsQuery | null | undefined
  badge: ReviewsTopRatedBadgeProgressQuery | null | undefined
  failed: { latest: boolean; growth: boolean; facets: boolean; badge: boolean }
}

type GrowthRow = { period?: string | null; newReviews?: number | null }
type CategoryRow = { period?: string | null; categorySlug?: string | null; newReviewCountByPurpose?: number | null }
type MixRow = {
  segmentType?: string | null
  segmentValue?: string | null
  reviewCount?: number | null
  toolDimReviews?: number | null
  categoryReviewCount?: number | null
  categoryDimReviews?: number | null
}
type SentimentRow = {
  period?: string | null
  attribute?: string | null
  positiveMentions?: number | null
  negativeMentions?: number | null
  totalMentions?: number | null
  reviewsWithPositiveMention?: number | null
  reviewsWithNegativeMention?: number | null
  reviewCount?: number | null
}
type SentimentTotals = { positive: number; negative: number; total: number; positiveReviews: number; negativeReviews: number }
type NpsRow = {
  segmentType?: string | null
  segmentValue?: string | null
  reviewCount?: number | null
  promoters?: number | null
  detractors?: number | null
}
type SegmentSums = { count: number; categoryCount: number; scored: number; promoters: number; detractors: number }
type SegmentTotals = { buckets: Map<string, SegmentSums>; dim: number; categoryDim: number }
type Bucket = (value: string) => string | null

const COMPANY_SIZE_KEYS: ReviewsCompanySizeKey[] = ['small', 'medium', 'large', 'enterprise']

const COMPANY_SIZES: Record<string, ReviewsCompanySizeKey> = {
  '0_10': 'small',
  '11_50': 'small',
  '51_100': 'medium',
  '101_200': 'medium',
  '201_500': 'large',
  '501_1000': 'large',
  '1001_2000': 'enterprise',
  '2001_5000': 'enterprise',
  '5001_10000': 'enterprise',
  '10001_': 'enterprise',
}

const SENIORITIES: { key: ReviewsSeniorityKey; value: string }[] = [
  { key: 'cLevel', value: 'C-Level' },
  { key: 'leadership', value: 'Leadership' },
  { key: 'manager', value: 'Manager' },
  { key: 'ic', value: 'Individual Contributor' },
]

const TOP_INDUSTRIES = 5
const MIN_SEGMENT_REVIEWS = 20
const EMPTY_SUMS: SegmentSums = { count: 0, categoryCount: 0, scored: 0, promoters: 0, detractors: 0 }
const MIN_MENTIONS = 10
const TOP_POSITIVE_CHIPS = 5
const TOP_NEGATIVE_CHIPS = 4

const EMPTY_LABEL = '—'

const share = (part: number, whole: number): number | null => (whole > 0 ? part / whole : null)

const countOf = <Key>(facet: { key: Key; docCount: number }[], keys: Key[]): number =>
  facet.reduce((sum, bucket) => (keys.includes(bucket.key) ? sum + bucket.docCount : sum), 0)

const sumNewReviews = (rows: GrowthRow[], from: string, to: string): number =>
  rows.reduce((sum, row) => (row.period && row.period >= from && row.period <= to ? sum + (row.newReviews ?? 0) : sum), 0)

const sumNewReviewsByPurpose = (rows: CategoryRow[], slug: string, from: string, to: string): number =>
  rows.reduce(
    (sum, row) =>
      row.categorySlug === slug && row.period && row.period >= from && row.period <= to ? sum + (row.newReviewCountByPurpose ?? 0) : sum,
    0,
  )

const points = (value: number): number => Math.round(value) || 0

const netScore = (plus: number, minus: number, total: number): number => points(((plus - minus) / total) * 100)

const seniorityBucket: Bucket = (value) => SENIORITIES.find((level) => level.value === value)?.key ?? null
const companySizeBucket: Bucket = (value) => COMPANY_SIZES[value] ?? null
const industryBucket: Bucket = (value) => value || null

const addSums = (sum: SegmentSums, add: Partial<SegmentSums>): SegmentSums => ({
  count: sum.count + (add.count ?? 0),
  categoryCount: sum.categoryCount + (add.categoryCount ?? 0),
  scored: sum.scored + (add.scored ?? 0),
  promoters: sum.promoters + (add.promoters ?? 0),
  detractors: sum.detractors + (add.detractors ?? 0),
})

const snapshotTotals = (mix: MixRow[], nps: NpsRow[], segmentType: string, bucket: Bucket): SegmentTotals => {
  const buckets = new Map<string, SegmentSums>()
  const mixRows = mix.filter((row) => row.segmentType === segmentType)
  for (const row of mixRows) {
    const key = bucket(row.segmentValue ?? '')
    if (key)
      buckets.set(
        key,
        addSums(buckets.get(key) ?? EMPTY_SUMS, { count: row.reviewCount ?? 0, categoryCount: row.categoryReviewCount ?? 0 }),
      )
  }
  for (const row of nps.filter((row) => row.segmentType === segmentType)) {
    const key = bucket(row.segmentValue ?? '')
    if (key) {
      buckets.set(
        key,
        addSums(buckets.get(key) ?? EMPTY_SUMS, {
          scored: row.reviewCount ?? 0,
          promoters: row.promoters ?? 0,
          detractors: row.detractors ?? 0,
        }),
      )
    }
  }
  return {
    buckets,
    dim: Math.max(0, ...mixRows.map((row) => row.toolDimReviews ?? 0)),
    categoryDim: Math.max(0, ...mixRows.map((row) => row.categoryDimReviews ?? 0)),
  }
}

const gained = (end: number, before: number): number => Math.max(0, end - before)

const gainedSums = (end: SegmentSums, before: SegmentSums): SegmentSums => ({
  count: gained(end.count, before.count),
  categoryCount: gained(end.categoryCount, before.categoryCount),
  scored: gained(end.scored, before.scored),
  promoters: gained(end.promoters, before.promoters),
  detractors: gained(end.detractors, before.detractors),
})

const windowTotals = (end: SegmentTotals, before: SegmentTotals): SegmentTotals => ({
  buckets: new Map([...end.buckets].map(([key, sums]) => [key, gainedSums(sums, before.buckets.get(key) ?? EMPTY_SUMS)])),
  dim: gained(end.dim, before.dim),
  categoryDim: gained(end.categoryDim, before.categoryDim),
})

const shareOf = (part: number, whole: number): number | null => (whole >= MIN_SEGMENT_REVIEWS ? part / whole : null)

const npsOf = ({ promoters, detractors, scored }: SegmentSums): number | null =>
  scored >= MIN_SEGMENT_REVIEWS ? netScore(promoters, detractors, scored) : null

const segmentRow = (key: string, label: string | null, sums: SegmentSums, { dim, categoryDim }: SegmentTotals): ReviewsSegmentRow => ({
  key,
  label,
  share: shareOf(sums.count, dim),
  categoryShare: shareOf(sums.categoryCount, categoryDim),
  nps: npsOf(sums),
})

const fixedSegmentRows = (keys: string[], totals: SegmentTotals): ReviewsSegmentRow[] =>
  keys.map((key) => segmentRow(key, null, totals.buckets.get(key) ?? EMPTY_SUMS, totals))

const industrySegmentRows = (totals: SegmentTotals): ReviewsSegmentRow[] => {
  const ranked = [...totals.buckets].sort(([, a], [, b]) => b.count - a.count)
  const top = ranked.slice(0, TOP_INDUSTRIES)
  const rows = top.map(([label, sums]) => segmentRow(camelCase(label), label, sums, totals))
  if (ranked.length <= TOP_INDUSTRIES) return rows
  const topSums = top.map(([, sums]) => sums).reduce(addSums, EMPTY_SUMS)
  const restSums = ranked
    .slice(TOP_INDUSTRIES)
    .map(([, sums]) => sums)
    .reduce(addSums, EMPTY_SUMS)
  const other = { ...restSums, count: gained(totals.dim, topSums.count), categoryCount: gained(totals.categoryDim, topSums.categoryCount) }
  return [...rows, { ...segmentRow('other', null, other, totals), other: true }]
}

const stateOf = (failed: boolean, present: boolean): InsightsMetricState => (failed ? 'error' : present ? 'ready' : 'empty')

const sentimentTotals = (rows: SentimentRow[]): [ReviewsAttributeKey, SentimentTotals][] => {
  const totals = new Map<ReviewsAttributeKey, SentimentTotals>()
  for (const row of rows) {
    if (!row.attribute) continue
    const key = row.attribute as ReviewsAttributeKey
    const sum = totals.get(key) ?? { positive: 0, negative: 0, total: 0, positiveReviews: 0, negativeReviews: 0 }
    totals.set(key, {
      positive: sum.positive + (row.positiveMentions ?? 0),
      negative: sum.negative + (row.negativeMentions ?? 0),
      total: sum.total + (row.totalMentions ?? 0),
      positiveReviews: sum.positiveReviews + (row.reviewsWithPositiveMention ?? 0),
      negativeReviews: sum.negativeReviews + (row.reviewsWithNegativeMention ?? 0),
    })
  }
  return [...totals].filter(([, { total }]) => total >= MIN_MENTIONS)
}

const sentimentRows = (totals: [ReviewsAttributeKey, SentimentTotals][]): ReviewsSentimentRow[] =>
  totals
    .map(([key, { positive, negative, total }]) => ({ key, score: netScore(positive, negative, total) }))
    .sort((a, b) => b.score - a.score)

const windowReviews = (rows: SentimentRow[]): number => {
  const byMonth = new Map<string, number>()
  for (const row of rows) if (row.period) byMonth.set(row.period, row.reviewCount ?? 0)
  return [...byMonth.values()].reduce((sum, count) => sum + count, 0)
}

const sentimentChips = (
  totals: [ReviewsAttributeKey, SentimentTotals][],
  reviews: number,
  pick: (sums: SentimentTotals) => number,
  limit: number,
): ReviewsSentimentChip[] =>
  reviews > 0
    ? totals
        .map(([key, sums]) => ({ key, share: Math.min(100, Math.round((pick(sums) / reviews) * 100)) }))
        .filter(({ share }) => share > 0)
        .sort((a, b) => b.share - a.share || a.key.localeCompare(b.key))
        .slice(0, limit)
    : []

const listedSince = (product: ProductById, date: string): boolean => !!product.publishedAt && product.publishedAt.slice(0, 10) <= date

const newReviewsDelta = (current: number, previous: number, locale: string): ReviewsInsightsView['timeframe']['newReviews']['delta'] => {
  const ratio = deltaToRatio(current, current - previous)
  if (ratio == null || ratio === 0) return null
  return { value: makeSignedPercent(locale)(ratio), direction: ratio < 0 ? 'down' : 'up' }
}

export const mapReviewsInsights = (
  { latest, growth, facets, badge, failed }: ReviewsInsightsSources,
  { product, growthWindow, locale }: { product: ProductById; growthWindow: ReviewsGrowthWindow; locale: string },
): ReviewsInsightsView => {
  const number = makeNumber(locale)
  const percent = makeRatioPercent(locale, 0)

  const [freshnessItem, , summaryItem, campaignsItem, countTiersItem] = failed.latest ? [] : (latest?.manage?.insights?.results ?? [])
  const [growthItem, sentimentItem, , categoryItem, mixEndItem, mixBeforeItem, npsEndItem, npsBeforeItem] = failed.growth
    ? []
    : (growth?.manage?.insights?.results ?? [])
  const freshnessRows = freshnessItem?.response?.__typename === 'ManageInsightsReviewFreshnessResponse' ? freshnessItem.response.rows : []
  const growthRows = growthItem?.response?.__typename === 'ManageInsightsToolReviewGrowthResponse' ? growthItem.response.rows : []
  const countTiers =
    requestState(countTiersItem) !== 'error' && countTiersItem?.response?.__typename === 'ManageInsightsBenchmarkTiersResponse'
      ? countTiersItem.response.rows.at(-1)
      : undefined
  const mixRowsOf = (item: typeof mixEndItem): MixRow[] =>
    item?.response?.__typename === 'ManageInsightsReviewerMixResponse' ? item.response.rows : []
  const npsRowsOf = (item: typeof npsEndItem): NpsRow[] =>
    item?.response?.__typename === 'ManageInsightsNpsBySegmentResponse' ? item.response.rows : []
  const mixEndRows = mixRowsOf(mixEndItem)
  const mixBeforeRows = mixRowsOf(mixBeforeItem)
  const npsEndRows = npsRowsOf(npsEndItem)
  const npsBeforeRows = npsRowsOf(npsBeforeItem)
  const campaignRows: ReviewsCampaignRow[] =
    campaignsItem?.response?.__typename === 'ManageInsightsCampaignPerformanceResponse'
      ? campaignsItem.response.rows.flatMap((row) =>
          row.campaignId == null
            ? []
            : [
                {
                  id: row.campaignId,
                  title: row.campaignTitle ?? '',
                  status: (row.campaignStatus ?? null) as IncentivizationCampaignStatusEnum | null,
                  validFrom: row.campaignValidFrom ?? null,
                  validTo: row.campaignValidTo ?? null,
                  started: row.startedCount ?? 0,
                  submitted: row.submittedCount ?? 0,
                  published: row.publishedCount ?? 0,
                  rate: row.submitRate ?? null,
                },
              ],
        )
      : []
  const attributeRows =
    sentimentItem?.response?.__typename === 'ManageInsightsAttributeSentimentResponse' ? sentimentItem.response.rows : []

  const surveyResponses = failed.facets ? undefined : facets?.manage
  const states = surveyResponses?.all?.stateFacet ?? []
  const summary = summaryItem?.response?.__typename === 'ManageInsightsRatingSummaryResponse' ? summaryItem.response.rows[0] : undefined
  const outdatedShare = freshnessRows.find((row) => row.categorySlug === product.mainCategory.slug)?.outdatedReviewShare
  const ring = (key: ReviewsQualityRingKey, value: number | null): ReviewsQualityRing => ({
    key,
    value,
    label: value == null ? EMPTY_LABEL : value > 0 && percent(value) === percent(0) ? `<${percent(0.01)}` : percent(value),
  })
  const rings = [
    ring(
      'completion',
      share(
        countOf(states, [SurveyResponsesStateEnum.Published]),
        countOf(states, [SurveyResponsesStateEnum.Drafted, SurveyResponsesStateEnum.Published]),
      ),
    ),
    ring('response', summary?.replyRate ?? null),
    ring('freshness', outdatedShare == null ? null : 1 - outdatedShare),
  ]

  const current = sumNewReviews(growthRows, growthWindow.start, growthWindow.end)
  const { previousStart, previousEnd } = growthWindow
  const previous =
    previousStart && previousEnd && listedSince(product, previousStart) ? sumNewReviews(growthRows, previousStart, previousEnd) : null

  const progress = failed.badge ? undefined : badge?.manage?.topRatedBadgeProgress
  const rounded = (value: number | null | undefined): number | null => (value == null ? null : Math.round(value))

  const categoryTitle = (slug: string): string => product.categories.find((category) => category.slug === slug)?.title ?? slug
  const categoryWindowRows: CategoryRow[] =
    categoryItem?.response?.__typename === 'ManageInsightsReviewFreshnessResponse' ? categoryItem.response.rows : []
  const categorySlugs = new Set([
    ...product.categories.map((category) => category.slug),
    ...categoryWindowRows.flatMap((row) => (row.categorySlug ? [row.categorySlug] : [])),
  ])
  const categoryRows = [...categorySlugs]
    .map((slug) => ({
      label: categoryTitle(slug),
      value: sumNewReviewsByPurpose(categoryWindowRows, slug, growthWindow.start, growthWindow.end),
    }))
    .sort((a, b) => b.value - a.value)

  const sentimentByAspect = sentimentTotals(attributeRows)
  const sentiment = sentimentRows(sentimentByAspect)
  const sentimentReviews = windowReviews(attributeRows)

  const mixFailed = requestState(mixEndItem) === 'error' || requestState(mixBeforeItem) === 'error'
  const npsFailed = requestState(npsEndItem) === 'error' || requestState(npsBeforeItem) === 'error'
  const segmentsOf = (segmentType: string, bucket: Bucket, rows: (totals: SegmentTotals) => ReviewsSegmentRow[]): ReviewsSegmentRows => {
    const totals = windowTotals(
      snapshotTotals(mixEndRows, npsEndRows, segmentType, bucket),
      snapshotTotals(mixBeforeRows, npsBeforeRows, segmentType, bucket),
    )
    return {
      rows: rows(totals),
      shareState: stateOf(mixFailed, totals.dim > 0),
      npsState: stateOf(
        npsFailed,
        [...totals.buckets.values()].some((sums) => sums.scored > 0),
      ),
    }
  }
  return {
    quality: {
      rings,
      state: requestState(freshnessItem) === 'error' || requestState(summaryItem) === 'error' || failed.facets ? 'error' : 'ready',
    },
    timeframe: {
      totalReviews: {
        value: summary ? number(summary.reviewCount ?? 0) : EMPTY_LABEL,
        count: summary?.reviewCount ?? null,
        state: requestState(summaryItem),
      },
      newReviews: {
        value: `+${number(current)}`,
        delta: previous == null ? null : newReviewsDelta(current, previous, locale),
        state: requestState(growthItem),
      },
      benchmark: {
        top3: rounded(countTiers?.tier1Avg),
        top10: rounded(countTiers?.tier2Avg),
        categoryAvg: rounded(countTiers?.categoryAvg),
        median: countTiers?.categoryMedian ? Math.round(countTiers.categoryMedian) : null,
        state: metricState(countTiersItem, countTiers ? 1 : 0),
      },
      badge: {
        reviewCount: progress?.reviewCount ?? 0,
        minimumReviewCount: progress?.minimumReviewCount ?? 0,
        averageScore: progress?.averageScore ?? null,
        eligible: progress?.eligible ?? false,
        state: failed.badge ? 'error' : progress ? 'ready' : 'empty',
      },
    },
    byCategory: {
      rows: categoryRows,
      weakest: categoryRows.length > 1 ? (categoryRows.at(-1)?.label ?? null) : null,
      state: metricState(categoryItem, categoryRows.length),
    },
    bySegment: {
      seniority: segmentsOf('seniority', seniorityBucket, (totals) =>
        fixedSegmentRows(
          SENIORITIES.map(({ key }) => key),
          totals,
        ),
      ),
      industry: segmentsOf('industry', industryBucket, industrySegmentRows),
      companySize: segmentsOf('company_size', companySizeBucket, (totals) => fixedSegmentRows(COMPANY_SIZE_KEYS, totals)),
    },
    sentiment: {
      rows: sentiment,
      chips: {
        positive: sentimentChips(sentimentByAspect, sentimentReviews, (sums) => sums.positiveReviews, TOP_POSITIVE_CHIPS),
        negative: sentimentChips(sentimentByAspect, sentimentReviews, (sums) => sums.negativeReviews, TOP_NEGATIVE_CHIPS),
      },
      state: metricState(sentimentItem, sentiment.length),
    },
    campaigns: { rows: campaignRows, state: metricState(campaignsItem, campaignRows.length) },
  }
}
